// Functions to get all the queries we need to satisfy the api
// api.ts
import axios, { AxiosError } from "axios";
import { tokenStorage } from "./tokenStorage.tsx";
import {
  GetWaterDropletsResponse,
  PostListenTimeRequest,
  GenericMessageResponse,
  GetLessonDataRequest,
  GetLessonDataResponse,
  PostActivityCompleteRequest,
  PostActivityCompleteResponse,
  PostTempUserResponse,
  PostTempUserRequest,
  PostLessonCompletedRequest,
  PostChapterCompletedResponse,
  PostChapterCompletedRequest,
  PutWordStatusRequest,
  PutWordStatusResponse,
  GetCourseInfoResponse,
  PostLessonCompletedResponse,
  GetPlantStatsResponse,
  GetCardsResponse,
  DeleteCardRequest,
  GetUserInfoResponse,
  GetUserStatsResponse,
  GetStatsTableRequest,
  GetStatsTableResponse,
  PutTempUserToFullUserRequest,
  GetLearningWordsDictionaryResponse,
  GetKnownWordsDictionaryResponse,
  GetCardCountResponse,
  GetDictionaryStatsResponse,
  GetAllLessonRequest,
  GetAllLessonsResponse,
  GetAllChaptersRequest,
  PutCardRequest,
  PutCardRequestResponse,
  ClientUpdateHeaderKey,
  GetAllCoursesResponse,
  PostCourseCompletedResponse,
  PostCourseCompletedRequest,
  PutNameRequest,
  StripeCheckoutResponse,
  GetAllCourseTreesResponse,
  PostWordErrorRequest,
  NPSCompletedResponse,
  GetPhraseRequest,
  GetPhraseResponse,
  PutPhraseSavedRequest,
  GetAllPhrasesResponse,
  PutUserPhraseStatusRequest,
  GetSearchWordsResponse,
  GetSearchPhrasesResponse,
  PostOnboardingRequest,
  Onboarding,
  GetUserSettingsResponse,
  PutUserSettingsRequest,
  GetAllCoursesProgressResponse,
  GetAllCourseTopicsResponse,
  GetAllCourseLevelsResponse,
  GetCompletedLessonsResponse,
  PostPlacementResultRequest,
} from "@shared_types";
import { API_BASE_URL, BAD_AUTH_REDIRECT_URL } from "./base.tsx";
import {
  deletePocketBaseUser,
  signOutUser,
  unlinkExternalAuths,
} from "./login.tsx";
import VERSION from "@/version";

export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

axiosInstance.interceptors.request.use((config) => {
  config.headers[ClientUpdateHeaderKey] = VERSION;
  return config;
});

export class AuthError extends Error {
  constructor(message: string) {
    super(message);
  }
}

// Error handling
function handleAxiosError(error: AxiosError): never {
  const status = error.response?.status;
  const unAuthorizedStatus = 401;

  // anytime an unathorized request goes through in our app,
  // this will redirect the user to where we want em
  if (status == unAuthorizedStatus) {
    // redirect user to onBoarding
    window.location.assign(BAD_AUTH_REDIRECT_URL);

    // clean the token from storage since its bad and wont be useful anymore
    tokenStorage.clearToken();
    throw new AuthError("Unauthorized");
  }

  let message = error.message; // Default to the error message from Axios

  if (error.response?.data && typeof error.response.data === "object") {
    const data = error.response.data as { message?: string };
    if (data.message) {
      message = data.message;
    }
  }

  console.error(`Error: ${message} (Status Code: ${status})`);
  throw new Error(message);
}

// this function checks to see if we are logged in.
// this means the token in local storage is valid
// we want to minimize reliance on pocketbase which has a
// changing api
export async function isLoggedIn(): Promise<boolean> {
  if (!tokenStorage.isTokenSet()) {
    return false;
  }
  const token = tokenStorage.getToken();
  const valid = await isTokenValid(token);
  return valid;
}

async function isTokenValid(token: string): Promise<boolean> {
  // try a small endpoint with auth
  // if we get a 401 return false
  // improvement could be to make an enpoint just for this,
  // incase this one changes
  try {
    await axiosInstance.get<GetWaterDropletsResponse>("/auth/waterdroplets", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return true;
  } catch (error) {
    const badAuth = (error as AxiosError).response?.status == 401;
    if (badAuth) {
      return false;
    }
    return true;
  }
}

// Define a generic type for the functions that will be passed into callWithAuth
type AuthFunction<T, R> = (args: { authToken: string; data?: T }) => Promise<R>;

// callWithAuth function definition
// It tries to read the token from local storage
// if its missing we will log an error and redirect.
// The request will be a type defined in this file for the request
// the browser to login page
// GetRequest need to use params
export function callWithAuth<T, R>(
  f: AuthFunction<T, R>,
  data?: T
): Promise<R> {
  if (!tokenStorage.isTokenSet()) {
    window.location.assign(BAD_AUTH_REDIRECT_URL);
  }
  const authToken = tokenStorage.getToken();
  const params: AuthFuncParams<T> = {
    authToken,
    data,
  };
  return f(params);
}

// All get has params and not request
export interface AuthFuncParams<T> {
  authToken: string;
  data?: T;
}

// API Request Functions
export async function postListenTime({
  data,
  authToken,
}: AuthFuncParams<PostListenTimeRequest>): Promise<GenericMessageResponse> {
  const request = data;
  try {
    const response = await axiosInstance.post<GenericMessageResponse>(
      "/auth/listentime",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function postNewUser({
  authToken,
}: AuthFuncParams<void>): Promise<GenericMessageResponse> {
  try {
    const response = await axiosInstance.post<GenericMessageResponse>(
      "/auth/newuser",
      undefined,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function deleteUser(): Promise<void> {
  try {
    const authToken = tokenStorage.getToken();
    await unlinkExternalAuths();
    await axiosInstance.delete<void>("/auth/deleteuser", {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    await deletePocketBaseUser();
    signOutUser();
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function putPlantTree({
  authToken,
}: AuthFuncParams<void>): Promise<GenericMessageResponse> {
  try {
    const response = await axiosInstance.put<GenericMessageResponse>(
      "/auth/planttree",
      undefined,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function postTempUser(
  data: PostTempUserRequest
): Promise<PostTempUserResponse> {
  const request = data;
  request.email = request.email.toLowerCase();
  try {
    const response = await axiosInstance.post<PostTempUserResponse>(
      "/guest/tempuser",
      request
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getLessonData({
  data,
  authToken,
}: AuthFuncParams<GetLessonDataRequest>): Promise<GetLessonDataResponse> {
  const params = data;
  try {
    const response = await axiosInstance.get<GetLessonDataResponse>(
      "/auth/lessondata",
      {
        params,
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    if (response.status === 403) {
      throw new Error("Forbidden");
    }
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function postChapterCompleted({
  data,
  authToken,
}: AuthFuncParams<PostChapterCompletedRequest>): Promise<PostChapterCompletedResponse> {
  const request = data;
  try {
    const response = await axiosInstance.post<PostChapterCompletedResponse>(
      "/auth/chaptercompleted",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}
export async function postCourseCompleted({
  data,
  authToken,
}: AuthFuncParams<PostCourseCompletedRequest>): Promise<PostCourseCompletedResponse> {
  const request = data;
  try {
    const response = await axiosInstance.post<PostCourseCompletedResponse>(
      "/auth/coursecompleted",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function postLessonCompleted({
  data,
  authToken,
}: AuthFuncParams<PostLessonCompletedRequest>): Promise<PostLessonCompletedResponse> {
  const request = data;
  try {
    const response = await axiosInstance.post<PostLessonCompletedResponse>(
      "/auth/lessoncompleted",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function putWordStatus({
  data,
  authToken,
}: AuthFuncParams<PutWordStatusRequest>): Promise<PutWordStatusResponse> {
  const request = data;
  try {
    const response = await axiosInstance.put<PutWordStatusResponse>(
      "/auth/wordstatus",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function postActivityComplete({
  data,
  authToken,
}: AuthFuncParams<PostActivityCompleteRequest>): Promise<PostActivityCompleteResponse> {
  const request = data;
  try {
    const response = await axiosInstance.post<PostActivityCompleteResponse>(
      "/auth/activitycomplete",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getAllCourses({
  authToken,
}: AuthFuncParams<void>): Promise<GetAllCoursesResponse> {
  try {
    const response = await axiosInstance.get<GetAllCoursesResponse>(
      "/auth/courses",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getAllCourseTrees({
  authToken,
}: AuthFuncParams<void>): Promise<GetAllCourseTreesResponse> {
  try {
    const response = await axiosInstance.get<GetAllCourseTreesResponse>(
      "/auth/coursetrees",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getAllLessons({
  data,
  authToken,
}: AuthFuncParams<GetAllLessonRequest>): Promise<GetAllLessonsResponse> {
  const params = data;
  try {
    const response = await axiosInstance.get<GetAllLessonsResponse>(
      "/auth/chapterinfo",
      {
        params,
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getAllChapters({
  data,
  authToken,
}: AuthFuncParams<GetAllChaptersRequest>): Promise<GetCourseInfoResponse> {
  const params = data;
  try {
    const response = await axiosInstance.get<GetCourseInfoResponse>(
      "/auth/courseinfo",
      {
        params,
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getPlantStats({
  authToken,
}: AuthFuncParams<void>): Promise<GetPlantStatsResponse> {
  try {
    const response = await axiosInstance.get<GetPlantStatsResponse>(
      "/auth/plantstats",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getDictionaryStats({
  authToken,
}: AuthFuncParams<void>): Promise<GetDictionaryStatsResponse> {
  try {
    const response = await axiosInstance.get<GetDictionaryStatsResponse>(
      "/auth/dictionarystats",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getKnownWordsDictionary({
  authToken,
  data,
}: AuthFuncParams<{
  page?: number;
  limit?: number;
}>): Promise<GetKnownWordsDictionaryResponse> {
  try {
    const response = await axiosInstance.get<GetKnownWordsDictionaryResponse>(
      "/auth/knownwords",
      {
        params: data,
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getLearningWordsDictionary({
  authToken,
  data,
}: AuthFuncParams<{
  page?: number;
  limit?: number;
}>): Promise<GetLearningWordsDictionaryResponse> {
  try {
    const response =
      await axiosInstance.get<GetLearningWordsDictionaryResponse>(
        "/auth/learningwords",
        {
          params: data,
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getReviewCards({
  authToken,
}: AuthFuncParams<void>): Promise<GetCardsResponse> {
  try {
    const response = await axiosInstance.get<GetCardsResponse>("/auth/cards", {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getCardCount({
  authToken,
}: AuthFuncParams<void>): Promise<GetCardCountResponse> {
  try {
    const response = await axiosInstance.get<GetCardCountResponse>(
      "/auth/card/count",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function putReviewCard({
  authToken,
  data,
}: AuthFuncParams<PutCardRequest>): Promise<PutCardRequestResponse> {
  try {
    const response = await axiosInstance.put<PutCardRequestResponse>(
      "/auth/card",
      data,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function deleteCard({
  authToken,
  data,
}: AuthFuncParams<DeleteCardRequest>): Promise<GenericMessageResponse> {
  try {
    const response = await axiosInstance.delete<GenericMessageResponse>(
      "/auth/deletecard/" + data?.cardId,
      {
        headers: { Authorization: `Bearer ${authToken}` },
        data: { card_id: data?.cardId },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getUserInfoNoRedirect({
  authToken,
}: AuthFuncParams<void>): Promise<GetUserInfoResponse | undefined> {
  try {
    const response = await axiosInstance.get<GetUserInfoResponse>(
      "/auth/userinfo",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    return undefined;
  }
}

export async function getUserInfo({
  authToken,
}: AuthFuncParams<void>): Promise<GetUserInfoResponse> {
  try {
    const response = await axiosInstance.get<GetUserInfoResponse>(
      "/auth/userinfo",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getUserStats({
  authToken,
}: AuthFuncParams<void>): Promise<GetUserStatsResponse> {
  try {
    const response = await axiosInstance.get<GetUserStatsResponse>(
      "/auth/userstats",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getStatsTable({
  data,
  authToken,
}: AuthFuncParams<GetStatsTableRequest>): Promise<GetStatsTableResponse> {
  const params = data;
  try {
    const response = await axiosInstance.get<GetStatsTableResponse>(
      "/auth/statstable",
      {
        params,
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function putTempUserToFullUser({
  data,
  authToken,
}: AuthFuncParams<PutTempUserToFullUserRequest>): Promise<GenericMessageResponse> {
  const request = data;
  try {
    const response = await axiosInstance.put<GenericMessageResponse>(
      "/auth/tempusertofulluser",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getWaterDroplets({
  authToken,
}: AuthFuncParams<any>): Promise<GetWaterDropletsResponse> {
  try {
    const response = await axiosInstance.get<GetWaterDropletsResponse>(
      "/auth/waterdroplets",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export interface PostFirebasePushTokenRequest {
  token: string;
}

export async function postFirebasePushToken({
  data,
  authToken,
}: AuthFuncParams<PostFirebasePushTokenRequest>): Promise<GenericMessageResponse> {
  const request = data;
  try {
    const response = await axiosInstance.post<GenericMessageResponse>(
      "/auth/firebase",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export interface PostAppleVerifyTransactionRequest {
  transactionId: string;
}

export async function postAppleVerifyTransaction({
  data,
  authToken,
}: AuthFuncParams<PostAppleVerifyTransactionRequest>): Promise<GenericMessageResponse> {
  const request = data;
  try {
    const response = await axiosInstance.post<GenericMessageResponse>(
      "/auth/apple/iap/verify",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

type CheckoutSessionRequest = {
  sku: string;
};

export async function checkoutSession({
  data,
  authToken,
}: AuthFuncParams<CheckoutSessionRequest>): Promise<StripeCheckoutResponse> {
  if (!data) {
    throw new Error("SKU is required");
  }
  try {
    const response = await axiosInstance.get<StripeCheckoutResponse>(
      "/auth/stripe/checkout/" + data.sku,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export interface SessionStatusRequest {
  sessionId: string;
  sku: string;
}

export interface SessionStatusResponse {
  sessionStatus: string;
  paymentStatus: string;
}

export async function sessionStatus({
  data,
  authToken,
}: AuthFuncParams<SessionStatusRequest>): Promise<SessionStatusResponse> {
  const request = data;
  if (!request) {
    throw new Error("Session ID and SKU are required");
  }
  try {
    const response = await axiosInstance.get<SessionStatusResponse>(
      "/auth/stripe/sessionstatus/" + request.sessionId,
      {
        params: {
          sku: request.sku,
        },
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export interface PostCreateCustomerPortalSessionResponse {
  url: string;
}

export async function postCreateCustomerPortalSession({
  authToken,
}: AuthFuncParams<void>): Promise<PostCreateCustomerPortalSessionResponse> {
  try {
    const response =
      await axiosInstance.post<PostCreateCustomerPortalSessionResponse>(
        "/auth/stripe/create-customer-portal-session",
        undefined,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export enum CardAction {
  Good = "good",
  Again = "again",
}

export interface GetCurrentCourseResponse {
  courseId: number;
}

const CURRENT_COURSE_KEY = "currentCourse";

/**
 * Get the current course the user is working on.
 */
export async function getCurrentCourse(): Promise<GetCurrentCourseResponse> {
  const currentCourse = localStorage.getItem(CURRENT_COURSE_KEY);
  return currentCourse ? JSON.parse(currentCourse) : { courseId: 1 };
}

export interface PutCurrentCourseRequest extends GetCurrentCourseResponse {}

/**
 * Set the current course the user is working on.
 */
export async function putCurrentCourse(
  data: PutCurrentCourseRequest
): Promise<void> {
  localStorage.setItem(CURRENT_COURSE_KEY, JSON.stringify(data));
}

const ACTIVE_COURSE_FILTERS_KEY = "activeCourseFilters";

/**
 * Get the active course filters.
 */
export async function getActiveCourseFilters(): Promise<Filters> {
  const filters = localStorage.getItem(ACTIVE_COURSE_FILTERS_KEY);
  return filters
    ? JSON.parse(filters)
    : { selectedLevel: 0, selectedTopics: [] };
}

export type Filters = {
  selectedLevel: number | null;
  selectedTopics: number[];
};
/**
 * Set the active course filters.
 */
export async function putActiveCourseFilters(data: Filters): Promise<void> {
  localStorage.setItem(ACTIVE_COURSE_FILTERS_KEY, JSON.stringify(data));
}

export async function putName({
  data,
  authToken,
}: AuthFuncParams<PutNameRequest>): Promise<GenericMessageResponse> {
  const request = data;
  try {
    const response = await axiosInstance.put<GenericMessageResponse>(
      "/auth/name",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export interface PostNPSRequest {
  rating: number;
}

export async function postNPS({
  data,
  authToken,
}: AuthFuncParams<PostNPSRequest>): Promise<GenericMessageResponse> {
  const request = data;
  try {
    const response = await axiosInstance.post<GenericMessageResponse>(
      "/auth/nps",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export interface PostNPSFeedbackRequest {
  feedback: string;
}

export async function postNPSFeedback({
  data,
  authToken,
}: AuthFuncParams<PostNPSFeedbackRequest>): Promise<GenericMessageResponse> {
  const request = data;
  try {
    const response = await axiosInstance.post<GenericMessageResponse>(
      "/auth/nps/feedback",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getShouldSeeNPS({
  authToken,
}: AuthFuncParams<void>): Promise<NPSCompletedResponse> {
  try {
    const response = await axiosInstance.get<NPSCompletedResponse>(
      "/auth/nps/should-see",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function postWordError({
  data,
  authToken,
}: AuthFuncParams<PostWordErrorRequest>): Promise<GenericMessageResponse> {
  const request = data;
  console.log("postWordError", request);
  try {
    const response = await axiosInstance.post<GenericMessageResponse>(
      "/auth/worderror",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getPhrase({
  data,
  authToken,
}: AuthFuncParams<GetPhraseRequest>): Promise<GetPhraseResponse> {
  try {
    const response = await axiosInstance.post<GetPhraseResponse>(
      "/auth/phrase",
      data,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function putPhraseSaved({
  data,
  authToken,
}: AuthFuncParams<PutPhraseSavedRequest>): Promise<GenericMessageResponse> {
  try {
    const response = await axiosInstance.put<GenericMessageResponse>(
      "/auth/phrase/saved",
      data,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function putUserPhraseStatus({
  data,
  authToken,
}: AuthFuncParams<PutUserPhraseStatusRequest>): Promise<GenericMessageResponse> {
  try {
    const response = await axiosInstance.put<GenericMessageResponse>(
      "/auth/phrase/status",
      data,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function deleteUserPhrase({
  data,
  authToken,
}: AuthFuncParams<{ phraseId: number }>): Promise<GenericMessageResponse> {
  if (!data?.phraseId) {
    throw new Error("Phrase ID is required");
  }
  try {
    const response = await axiosInstance.delete<GenericMessageResponse>(
      "/auth/phrase/" + data.phraseId,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getKnownPhrases({
  authToken,
  data,
}: AuthFuncParams<{
  page?: number;
  limit?: number;
}>): Promise<GetAllPhrasesResponse> {
  try {
    const params = new URLSearchParams();
    if (data) {
      if (data.page) params.append("page", data.page.toString());
      if (data.limit) params.append("limit", data.limit.toString());
    }

    const response = await axiosInstance.get<GetAllPhrasesResponse>(
      `/auth/phrases/known?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getLearningPhrases({
  authToken,
  data,
}: AuthFuncParams<{
  page?: number;
  limit?: number;
}>): Promise<GetAllPhrasesResponse> {
  try {
    const params = new URLSearchParams();
    if (data) {
      if (data.page) params.append("page", data.page.toString());
      if (data.limit) params.append("limit", data.limit.toString());
    }

    const response = await axiosInstance.get<GetAllPhrasesResponse>(
      `/auth/phrases/learning?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getVersion(): Promise<string> {
  try {
    const response = await axiosInstance.get<string>("/version");
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function searchWords({
  authToken,
  data,
}: AuthFuncParams<{
  status: string;
  query: string;
  page?: number;
  limit?: number;
}>): Promise<GetSearchWordsResponse> {
  try {
    const params = new URLSearchParams();
    if (data) {
      if (data.status) params.append("status", data.status);
      if (data.query) params.append("query", data.query);
      if (data.page) params.append("page", data.page.toString());
      if (data.limit) params.append("limit", data.limit.toString());
    }

    const response = await axiosInstance.get<GetSearchWordsResponse>(
      `/auth/words/search?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function searchPhrases({
  authToken,
  data,
}: AuthFuncParams<{
  status: string;
  query: string;
  page?: number;
  limit?: number;
}>): Promise<GetAllPhrasesResponse> {
  try {
    const params = new URLSearchParams();
    if (data) {
      if (data.status) params.append("status", data.status);
      if (data.query) params.append("query", data.query);
      if (data.page) params.append("page", data.page.toString());
      if (data.limit) params.append("limit", data.limit.toString());
    }

    const response = await axiosInstance.get<GetSearchPhrasesResponse>(
      `/auth/phrases/search?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function deleteUserWord({
  authToken,
  data,
}: AuthFuncParams<{ userWordId: number }>): Promise<GenericMessageResponse> {
  if (!data?.userWordId) {
    throw new Error("User word id is required");
  }
  try {
    const response = await axiosInstance.delete<GenericMessageResponse>(
      `/auth/word/${data.userWordId}`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function submitOnboarding({
  data,
  authToken,
}: AuthFuncParams<
  Omit<Onboarding, "id" | "userId" | "createdAt">
>): Promise<GenericMessageResponse> {
  try {
    const request: PostOnboardingRequest = {
      onboarding: data as Onboarding, // Type assertion as API expects the full type
    };

    const response = await axiosInstance.post<GenericMessageResponse>(
      "/auth/onboarding",
      request,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

// User Settings API calls
export async function getUserSettings({
  authToken,
}: AuthFuncParams<void>): Promise<GetUserSettingsResponse> {
  try {
    const response = await axiosInstance.get<GetUserSettingsResponse>(
      "/auth/user/settings",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function updateUserSettings({
  data,
  authToken,
}: AuthFuncParams<PutUserSettingsRequest>): Promise<GenericMessageResponse> {
  try {
    const response = await axiosInstance.put<GenericMessageResponse>(
      "/auth/user/settings",
      data,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getAllCoursesProgress({
  authToken,
}: AuthFuncParams<void>): Promise<GetAllCoursesProgressResponse> {
  try {
    const response = await axiosInstance.get<GetAllCoursesProgressResponse>(
      "/auth/coursesprogress",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getAllCourseTopics({
  authToken,
}: AuthFuncParams<void>): Promise<GetAllCourseTopicsResponse> {
  try {
    const response = await axiosInstance.get("/auth/coursetopics", {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getAllCourseLevels({
  authToken,
}: AuthFuncParams<void>): Promise<GetAllCourseLevelsResponse> {
  try {
    const response = await axiosInstance.get("/auth/courselevels", {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function getCompletedLessons({
  authToken,
}: AuthFuncParams<void>): Promise<GetCompletedLessonsResponse> {
  try {
    const response = await axiosInstance.get<GetCompletedLessonsResponse>(
      "/auth/completedlessons",
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}

export async function postPlacementResult({
  authToken,
  data,
}: AuthFuncParams<PostPlacementResultRequest>): Promise<GenericMessageResponse> {
  try {
    const response = await axiosInstance.post<GenericMessageResponse>(
      "/auth/user/placementresult",
      data,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error as AxiosError);
  }
}
